import { ApolloProvider } from '@apollo/client'
import LoadingEllipsis from 'client/components/common/LoadingEllipsis'
import { ScreenConnector } from 'client/components/Screen/ScreenConnector'
import ErrorBoundary from 'client/Other/ErrorBoundary'
import { shouldSeeSuperNavigation } from 'client/Other/functions/superNavigationFunctions'
import React, { lazy, Suspense } from 'react'
import { Provider } from 'react-redux'
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom'

import App from './App'
import MobileWelcome from './components/MobileWelcome'
import { Container } from './Global/Container'
import { Controller } from './Global/Controller'
import { restInvoiceClient } from './graphQueries'
import { NavigationInterface } from './Interfaces/NavigationInterface'
import { StateInterface } from './Interfaces/StateInterface'
import Blocked from './Other/Blocked'

const isMobileSize = () => innerWidth <= 750

export interface ApplicationStateInterface {
  isMobileContinue: boolean
}

interface IStateProps {
  navigation: NavigationInterface
  language: string
  showSuperNavigation: boolean
}

const mapStateToProps = (state: StateInterface) => ({
  navigation: state.navigation,
  language: state.company?.language,
  showSuperNavigation: shouldSeeSuperNavigation(state.company),
})

const Gallery = lazy(() => import('./components/common/Gallery'))
const Editor = lazy(() => import('./components/Editor/Editor'))
const InvoiceEditor = lazy(() => import('client/components/invoiceEditor/InvoiceEditorRoot'))
const Receipts = lazy(() => import('client/components/DocumentsV1/Receipt/List/Receipts'))
const DraftPreview = lazy(() => import('client/components/DraftPreview/DraftPreview'))
const Invoices = lazy(() => import('client/components/invoices/Invoices'))
const Estimates = lazy(() => import('client/components/estimates/Estimates'))
const Deliverynotes = lazy(() => import('client/components/DocumentsV1/Deliverynote/List/Deliverynotes'))
const BankingPage = lazy(() => import('client/components/banking/Banking'))
const RecurringProfiles = lazy(() => import('client/components/recurringProfiles/RecurringProfiles'))
const Products = lazy(() => import('client/components/products/Products'))
const EstimatePreview = lazy(() => import('client/components/EstimatePreview/EstimatePreview'))
const ReceiptPreview = lazy(() => import('client/components/DocumentsV1/Receipt/Preview/ReceiptPreview'))
const DeliverynotePreview = lazy(() => import('client/components/DocumentsV1/Deliverynote/Preview/DeliverynotePreview'))
const TrackerPage = lazy(() => import('client/components/Tracker/TrackerPage'))
const Reports = lazy(() => import('client/components/reports/Reports'))
const Customers = lazy(() => import('client/components/customers/Customers'))
const MyAccount = lazy(() => import('client/components/MyAccount/MyAccount'))
const MobileAccountSettings = lazy(() => import('client/components/MobileAccountSettings/MobileAccountSettings'))
const MobileInvoiceNumber = lazy(() => import('client/components/MobileInvoiceNumber/MobileInvoiceNumber'))
const Dashboard = lazy(() => import('./components/dashboard/Dashboard'))
const ReactModals = lazy(() => import('./components/Modal/ReactModals'))
const Expenses = lazy(() => import('client/components/expenses/Expenses'))
const AgerasAccounting = lazy(
  () => import('client/components/AgerasAccounting/AgerasAccountingTab/AgerasAccountingTab'),
)
const Oops = lazy(() => import('client/Other/Oops'))
const ExternalLogout = lazy(() => import('client/components/ExternalLogout/ExternalLogout'))

const Application = Controller(
  mapStateToProps,
  null,
)(
  class Application extends Container<IStateProps> {
    constructor(props) {
      super(props)
      this.state = {
        isMobileContinue: false,
      }
    }

    private onMobileContinue = () => {
      localStorage.setItem('memory-mobile-welcome', 'true')
      this.setState({ isMobileContinue: true })
    }

    private renderPath(path: string, component: JSX.Element) {
      return (
        <Route
          path={path}
          render={() => (
            <ErrorBoundary path={path} language={this.props?.language}>
              {component}
            </ErrorBoundary>
          )}
        />
      )
    }

    private isPageMobileOptimised() {
      return this.props.navigation.path.match(/MobileCompanySettings|MobilePaymentSettings|MobileInvoiceNumberSettings/)
    }

    public render() {
      return (
        <Router>
          <App navigation={this.props.navigation}>
            {isMobileSize() &&
              !this.isPageMobileOptimised() &&
              localStorage.getItem('memory-mobile-welcome') !== 'true' && (
                <MobileWelcome onMobileContinue={this.onMobileContinue} />
              )}
            <ScreenConnector />
            <div
              id="Routes"
              style={{
                height: 'calc(100vh - 40px)',
                overflow: 'auto',
                marginLeft: this.props.showSuperNavigation ? '54px' : '0',
              }}
            >
              <Suspense fallback={<LoadingEllipsis />}>
                <Switch>
                  {this.renderPath('/gallery', <Gallery />)}
                  {this.renderPath('/reactInvoice', <InvoiceEditor automation={'invoiceEditor'} />)}
                  {this.renderPath('/editor/:documentType/:documentSubtype/:documentId', <Editor />)}
                  {this.renderPath('/receipts/:documentType?/:documentId?', <Receipts automation={'receipts'} />)}
                  {this.renderPath('/DraftPreview', <DraftPreview automation="invoicePreview" draft={null} />)}
                  {this.renderPath('/reactInvoiceList/:documentType?/:documentId?', <Invoices />)}
                  {this.renderPath('/reactEstimateList/:documentType?/:documentId?', <Estimates />)}
                  {this.renderPath(
                    '/deliverynotes/:documentType?/:documentId?',
                    <Deliverynotes automation="deliverynotes" />,
                  )}
                  {this.renderPath('/banking/:transactionId?', <BankingPage />)}
                  {this.renderPath('/reactRecurringProfiles/:documentId?', <RecurringProfiles />)}
                  {this.renderPath('/productsAndServices/:productId?', <Products />)}
                  {this.renderPath('/EstimatePreview', <EstimatePreview automation="invoicePreview" />)}
                  {this.renderPath('/receipt-preview', <ReceiptPreview automation="ReceiptPreview" />)}
                  {this.renderPath('/deliverynote-preview', <DeliverynotePreview automation="DeliverynotePreview" />)}
                  {this.renderPath('/work', <TrackerPage />)}
                  {this.renderPath('/reactReports/:report?', <Reports />)}
                  {this.renderPath('/reactCustomers/:customerId?', <Customers />)}
                  {this.renderPath('/myAccount/:page?', <MyAccount />)}
                  {this.renderPath('/expenses/:transactionId?', <Expenses />)}
                  {this.renderPath('/MobileCompanySettings/:tradeNameId?', <MobileAccountSettings />)}
                  {this.renderPath('/MobilePaymentSettings', <MobileAccountSettings />)}
                  {this.renderPath('/MobileInvoiceNumberSettings', <MobileInvoiceNumber />)}
                  {this.renderPath('/agerasAccounting', <AgerasAccounting />)}
                  {this.renderPath('/agerasDirectAccounting', <AgerasAccounting />)}
                  {this.renderPath('/reactDashboard', <Dashboard />)}
                  {this.renderPath('/blocked', <Blocked />)}
                  {this.renderPath('/oops', <Oops language={this.props?.language} />)}
                  {this.renderPath('/logout', <ExternalLogout />)}
                  {this.renderPath('/', <Dashboard />)}
                </Switch>
              </Suspense>
            </div>
            <ErrorBoundary>
              <Suspense fallback={<div />}>
                <ReactModals />
              </Suspense>
            </ErrorBoundary>
          </App>
        </Router>
      )
    }
  },
)

const Routes = ({ store, brand }) => {
  return (
    <Provider store={store}>
      <ApolloProvider client={restInvoiceClient}>
        <Application history={history} brand={brand} />
      </ApolloProvider>
    </Provider>
  )
}

export default Routes
